import { Button } from '@pms-digital/design-system';
import { type ReactNode, useCallback, useId } from 'react';

/**
 * The shell of the three boxes of this screen (refonte modulaire §4; maquettes
 * `EnregistrerVersion`, `PasserEnProduction`).
 *
 * Written once for the three because they share the property that matters:
 * each one SHOWS THE EFFECT before it happens. §4 asks for that by name on the
 * pointer moves - « une confirmation qui montre l'effet : la version visée, les
 * référents qui la joueront, et la phrase "les sessions en cours ne changent
 * pas" » - and the save box and the retirement warning are the same object with
 * different content. Three spellings of a dialog would have drifted on the one
 * thing all three must not get wrong: the button naming the gesture rather than
 * saying « OK ».
 *
 * ## A native `<dialog open>` kept in flow by React state
 *
 * The régime `components/chat/close-confirm.tsx` settled, and its reasons hold
 * here: `open` rather than `showModal()`, so React owns the mounting; Escape
 * wired by hand, because `open` alone does not wire it; and the focus moved to
 * CANCEL on mount, so a stray Enter never commits a version, a pointer move or
 * a retirement.
 *
 * The veil is `--backdrop` - a token of `styles.css`, which is the one place a
 * dark surface is declared in a light-only application. The maquette draws the
 * same dark veil, and the token is why it can be quoted without a raw colour.
 *
 * ## Not the package `Dialog`, yet
 *
 * `Dialog` of `@pms-digital/design-system` has the focus (`initialFocus`), the
 * tones and the controlled `open` this shell needs, but no `data-testid`: the
 * mark of every box (`critere-boite-enregistrer`, `npc-boite-arret`...) frames
 * the title, the effect AND the buttons, and the unit suites and the e2e specs
 * scope their queries inside it. Its Radix content is also MODAL - it hides the
 * rest of the page from assistive tech and closes on a click outside - while
 * the pointer box opens from the non-modal history drawer. So the shell stays,
 * and only its buttons are the package's.
 *
 * ## Nothing closes a box whose gesture is in flight
 *
 * While `busy`, « Annuler » is disabled and Escape does nothing (second review
 * of !474): the request has left, cancelling cannot call it back, and a box
 * closed over it hands its doors to writes that race it - the save box's held
 * back autosave, a PUT beside the POST of the version. The box closes when the
 * answer lands, or stays with the refusal it brings.
 */

export interface VersionedDialogProps {
  title: string;
  /** The sentence under the title: what this gesture is about to do. */
  description: ReactNode;
  /** The effect, listed - referents, current pointer, the checkbox. */
  children?: ReactNode;
  confirmLabel: string;
  /** `destructive` for the retirement, which is the only irreversible-looking one. */
  confirmTone?: 'default' | 'destructive';
  busy: boolean;
  /** The refusal, worded by the caller from the server's `code`. Null when none. */
  error: string | null;
  confirmDisabled?: boolean;
  /**
   * What the cancel button says: « Annuler » by default, « Fermer » once the gesture has been
   * done and there is nothing left to cancel (the cascade box after a passage, issue #192).
   */
  cancelLabel?: string;
  /** No confirm button at all: the gesture is done and the box only waits to be closed. */
  confirmHidden?: boolean;
  /**
   * Why the confirm button is what it is - « Une ligne est refusée : la cascade part tout entière
   * ou pas du tout » - right above the buttons, where the eye is when it finds one inert.
   */
  note?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  'data-testid': string;
}

export function VersionedDialog({
  title,
  description,
  children,
  confirmLabel,
  confirmTone = 'default',
  busy,
  error,
  confirmDisabled = false,
  cancelLabel = 'Annuler',
  confirmHidden = false,
  note = null,
  onConfirm,
  onCancel,
  'data-testid': testId,
}: VersionedDialogProps) {
  const titleId = useId();
  // A `useCallback` ref rather than `autoFocus` (the a11y lint refuses it) and
  // rather than an effect - a stable callback runs on attach and on nothing else.
  const focusOnMount = useCallback((node: HTMLButtonElement | null) => node?.focus(), []);

  return (
    <div
      data-testid={testId}
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-[var(--backdrop)] p-5 py-10"
    >
      <dialog
        open
        aria-labelledby={titleId}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !busy) onCancel();
        }}
        className="relative m-0 w-full max-w-xl space-y-5 rounded-md border-2 border-gray-200 bg-white p-6 shadow-lg"
      >
        <div className="space-y-1">
          <h2 id={titleId} className="font-semibold text-bleu-nuit text-h4">
            {title}
          </h2>
          <p className="text-gray-700 text-small">{description}</p>
        </div>

        {children}

        {/* Mounted from the first render, empty or not: a live region created at
            the same moment it fills is not read out. */}
        <p aria-live="polite" className="text-rouge-deep text-small">
          {error}
        </p>

        {note === null ? null : (
          <p className="text-caption text-gray-600 leading-4 sm:text-right">{note}</p>
        )}

        <div className="flex flex-wrap justify-end gap-3">
          <Button
            ref={focusOnMount}
            type="button"
            variant="ghost"
            disabled={busy}
            onClick={onCancel}
          >
            {cancelLabel}
          </Button>
          {confirmHidden ? null : (
            <Button
              type="button"
              variant={confirmTone === 'destructive' ? 'destructive' : 'secondary'}
              disabled={busy || confirmDisabled}
              onClick={onConfirm}
            >
              {confirmLabel}
            </Button>
          )}
        </div>
      </dialog>
    </div>
  );
}

/**
 * One referent line inside a box: who plays this version, and under which tag.
 *
 * Exported because the three boxes all list referents and the list is the
 * « effet » they exist to show. `via` carries the intermediary when an
 * experience is reached through a protocol, and it is rendered in grey after
 * the name - « Expérience "Accueil PMS" par Entretien fondateur » - because it
 * explains the line rather than naming it.
 */
export function VersionedDialogSection({
  title,
  hint = null,
  children,
}: {
  title: string;
  /**
   * `string | null` and not an optional, because every caller computes it: an
   * optional prop plus `exactOptionalPropertyTypes` makes `hint={undefined}`
   * a type error, so a conditional hint would have to be spread in. `null` is
   * the repo's spelling of « deliberately absent ».
   */
  hint?: string | null;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <p className="font-semibold text-caption text-gray-600 uppercase tracking-wide">{title}</p>
      {children}
      {hint === null ? null : <p className="text-caption text-gray-600 leading-4">{hint}</p>}
    </div>
  );
}
